import { type CSSProperties, type ReactNode } from 'react';

import { type BookmakersConfig } from '@cx-modules/types';

import type ODDS_FORMAT from '~/enums/odds_format';
import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import { type BettingInsightsContent, type EsIndex } from '../types';

export interface BettingInsightsTableCellConfig<TIndex extends EsIndex> {
	alignItems?: CSSProperties['alignItems'];
	className?: string;
	highlight?: (data: { content: BettingInsightsContent<TIndex> }) => string;
	isFullWidthMobile?: boolean;
	isMain?: boolean;
	justifyContent?: CSSProperties['justifyContent'];
	key: string;
	order?: number;
	render: (
		this: BettingInsightsTableCell<TIndex>,
		data: {
			bookmakers: BookmakersConfig;
			content: BettingInsightsContent<TIndex>;
			oddsFormat: ODDS_FORMAT;
			resultIndex: number;
		},
	) => ReactNode;
	start?: number;
}

export class BettingInsightsTableCell<TIndex extends EsIndex>
	extends BettingInsightsComponent
	implements BettingInsightsTableCellConfig<TIndex>
{
	alignItems?: CSSProperties['alignItems'];
	className?: string;
	highlight?: (data: { content: BettingInsightsContent<TIndex> }) => string;
	isFullWidthMobile: boolean;
	isMain: boolean;
	justifyContent?: CSSProperties['justifyContent'];
	key: string;
	order?: number;
	render: (
		this: BettingInsightsTableCell<TIndex>,
		data: {
			bookmakers: BookmakersConfig;
			content: BettingInsightsContent<TIndex>;
			oddsFormat: ODDS_FORMAT;
			resultIndex: number;
		},
	) => ReactNode;
	start?: number;

	constructor({
		alignItems,
		className,
		highlight,
		isFullWidthMobile,
		isMain,
		justifyContent,
		key,
		order,
		render,
		start,
		...requestContext
	}: BettingInsightsTableCellConfig<TIndex> & RequestContext) {
		super(requestContext);
		this.key = key;
		this.isFullWidthMobile = isFullWidthMobile ?? false;
		this.className = className ?? '';
		this.isMain = isMain ?? false;
		this.alignItems = alignItems;
		this.highlight = highlight?.bind(this);
		this.justifyContent = justifyContent;
		this.order = order;
		this.render = render.bind(this);
		this.start = start;
	}

	static withRequestContext<TIndex extends EsIndex>(
		requestContext: RequestContext,
		config: BettingInsightsTableCellConfig<TIndex>,
	) {
		return new this<TIndex>({
			...requestContext,
			...config,
		});
	}
}
